import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
  forwardRef,
  inject,
  input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import {
  BehaviorSubject,
  Subscription,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  shareReplay,
  skip,
  startWith,
  take,
  tap,
} from 'rxjs';

import { isDefined } from '@shared/utils/is-defined';

import { MatDateRangePicker } from '../../datepicker';
import {
  DATE_RANGE_SELECTION_MAX_LENGTH_OWNER,
  DateRangeSelectionMaxLengthOwner,
} from '../../datepicker/date-range-selection-max-length-owner.token';
import { DateRange, DateRangePreset } from './date-range-input.view-model';

type Size = 'medium' | 'large';

@Component({
  selector: 'app-date-range-input',
  templateUrl: './date-range-input.component.html',
  styleUrls: ['./date-range-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      multi: true,
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateRangeInputComponent),
    },
    {
      provide: DATE_RANGE_SELECTION_MAX_LENGTH_OWNER,
      useExisting: forwardRef(() => DateRangeInputComponent),
    },
  ],
  standalone: false,
})
export class DateRangeInputComponent implements ControlValueAccessor, AfterViewInit, OnDestroy, DateRangeSelectionMaxLengthOwner<DateTime> {
  readonly #destroyRef = inject(DestroyRef);
  public inputClasses = input<string>('');
  public label = input<string>('');
  public isRequired = input(false);
  public error = input<string>();
  public errorInFlow = input(false);
  @Input() public startPlaceholder = '';
  @Input() public endPlaceholder = '';
  @Input() public errorMessage = '';
  @Input() public warningMessage = '';
  @Input() public hintMessage = '';
  @Input() public isValid: boolean | undefined = undefined;
  @Input() public isWarning = false;
  @Input() public isAction = false;
  @Input() public datepickerTitle: string | undefined = undefined;
  @Input() public onlyView: 'month' | 'year' | 'multi-year' | undefined = undefined;
  @Input() public minDate: DateTime | undefined = undefined;
  @Input() public maxDate: DateTime | undefined = undefined;
  @Input() public maxLengthInDays: number | undefined;
  @Input() public maxLengthInYears: number | undefined;
  @Input() public optional = false;
  @Input() public allowFreeText = false;
  public readonly isLoading = input<boolean>(false);
  @ViewChild(MatDateRangePicker) private readonly picker: MatDateRangePicker<DateRange> | null = null;
  @ViewChild('startInput', {
    read: ElementRef,
  })
  private readonly startInput?: ElementRef<HTMLInputElement>;
  @ViewChild('endInput', {
    read: ElementRef,
  })
  private readonly endInput?: ElementRef<HTMLInputElement>;

  public range = new FormGroup<{
    start: FormControl<DateTime | null>;
    end: FormControl<DateTime | null>;
  }>({
    start: new FormControl<DateTime | null>(null),
    end: new FormControl<DateTime | null>(null),
  });
  public isDisabled = false;
  public calendarIsOpen$ = new BehaviorSubject(false);

  private _size: Size = 'medium';
  private _calculatedSize: Size = this._size;

  private temporaryMinDateAccordingToMaxLengthConstraint: DateTime | undefined;
  private temporaryMaxDateAccordingToMaxLengthConstraint: DateTime | undefined;

  private onChange: null | ((arg: DateRange | null) => unknown) = null;
  private onTouch: null | (() => unknown) = null;

  private writeValueInProgress = false;

  /* private readonly defaultDateRangePresets = [
    { range: todayDateRange, label: this.#translateService.instant('shared.date-input.ranges.today') },
    { range: tomorrowDateRange, label: this.#translateService.instant('shared.date-input.ranges.tomorrow') },
    { range: currentWeekDateRange, label: this.#translateService.instant('shared.date-input.ranges.current-week') },
    { range: currentMonthDateRange, label: this.#translateService.instant('shared.date-input.ranges.current-month') },
  ]; */

  private _dateRangePresets: DateRangePreset[] | undefined = [];

  private readonly blurOrFocusInputsSubject$ = new BehaviorSubject<void>(void 0);
  private readonly inputsAreFocused$ = this.blurOrFocusInputsSubject$.pipe(
    debounceTime(100),
    map(() => {
      return document.activeElement === this.startInput?.nativeElement || document.activeElement === this.endInput?.nativeElement;
    }),
    shareReplay({
      refCount: true,
      bufferSize: 1,
    })
  );
  private readonly componentIsBlurred$ = combineLatest([this.inputsAreFocused$, this.calendarIsOpen$.pipe(map((isOpen) => !isOpen))]).pipe(
    map(([inputsAreFocused, calendarIsClosed]) => {
      return !inputsAreFocused && calendarIsClosed;
    }),
    distinctUntilChanged()
  );

  private nextBlurEmissionScheduledSubscription?: Subscription;

  constructor(
    private readonly chRef: ChangeDetectorRef,
    private readonly translateService: TranslateService
  ) {}

  public get minimumDate(): DateTime | undefined {
    if (
      this.temporaryMinDateAccordingToMaxLengthConstraint &&
      (!this.minDate || this.temporaryMinDateAccordingToMaxLengthConstraint > this.minDate)
    ) {
      return this.temporaryMinDateAccordingToMaxLengthConstraint;
    }

    return this.minDate;
  }

  public get maximumDate(): DateTime | undefined {
    if (
      this.temporaryMaxDateAccordingToMaxLengthConstraint &&
      (!this.maxDate || this.temporaryMaxDateAccordingToMaxLengthConstraint < this.maxDate)
    ) {
      return this.temporaryMaxDateAccordingToMaxLengthConstraint;
    }

    return this.maxDate;
  }

  get isValue(): boolean {
    return !!this.range?.value?.start || !!this.range?.value?.end;
  }

  public get dateRangePresets(): DateRangePreset[] | undefined {
    return this._dateRangePresets;
  }

  @Input() public set dateRangePresets(dateRangePresets: DateRangePreset[] | undefined) {
    this._dateRangePresets = dateRangePresets?.length === 0 ? undefined : (dateRangePresets ?? []);
  }

  public get size(): Size {
    return this._calculatedSize;
  }

  @Input()
  public set size(size: Size) {
    this._size = size;
    this.calculateSize();
  }

  public ngOnDestroy(): void {
    this.nextBlurEmissionScheduledSubscription?.unsubscribe();
    this.blurOrFocusInputsSubject$.complete();
  }

  public ngAfterViewInit(): void {
    this.watchPickerOpenState();
    this.watchFormChanges();
  }

  public writeValue(value: DateRange): void {
    // setting the range value even with emitEvent false still results in a valueChanges emit, so we have to filter it out manually
    this.writeValueInProgress = true;
    if (!isDefined(value)) {
      this.range.reset(undefined, {
        emitEvent: false,
      });
      this.writeValueInProgress = false;
      return;
    }

    this.range.setValue(value, {
      emitEvent: false,
    });
    this.writeValueInProgress = false;
  }

  public registerOnChange(fn: () => unknown): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => unknown): void {
    this.onTouch = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (this.isDisabled) {
      this.range.controls.start.disable({
        emitEvent: false,
      });
      this.range.controls.end.disable({
        emitEvent: false,
      });
    } else {
      this.range.controls.start.enable({
        emitEvent: false,
      });
      this.range.controls.end.enable({
        emitEvent: false,
      });
    }
    this.chRef.detectChanges();
  }

  public onInputFocusOrBlur(): void {
    this.blurOrFocusInputsSubject$.next();
  }

  public onBlur(): void {
    this.onTouch?.();
  }

  public writeTemporaryMinMaxValues(minDate?: DateTime, maxDate?: DateTime): void {
    this.temporaryMinDateAccordingToMaxLengthConstraint = minDate;
    this.temporaryMaxDateAccordingToMaxLengthConstraint = maxDate;
    this.chRef.markForCheck();
  }

  private watchPickerOpenState(): void {
    this.picker?.openedStream.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
      this.calendarIsOpen$.next(true);
      this.chRef.detectChanges();
    });
    this.picker?.closedStream.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
      if (!this.range.value.end) {
        this.range.controls.end.patchValue(this.range.controls.start.value);
      }

      this.writeTemporaryMinMaxValues();

      this.calendarIsOpen$.next(false);
      this.chRef.detectChanges();
    });
  }

  private watchFormChanges() {
    /** Changing the min, maxDate inputs during range selection causes the range form group to emit its current value again
     * That's why we need to filter out these false change emissions.
     */
    const DATE_RANGE_CHANGE_DEBOUNCE_TIME = 100;
    this.range.valueChanges
      .pipe(
        filter(() => !this.writeValueInProgress),
        tap(() => {
          this.nextBlurEmissionScheduledSubscription?.unsubscribe();
        }),
        debounceTime(DATE_RANGE_CHANGE_DEBOUNCE_TIME),
        startWith(this.range.value),
        pairwise(),
        filter(([oldRange, newRange]) => {
          return JSON.stringify(oldRange) !== JSON.stringify(newRange);
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(([_, { start, end }]) => {
        this.scheduleRangeEmissionOnNextBlur({
          start: start!,
          end: end!,
        });
      });

    this.componentIsBlurred$
      .pipe(
        skip(1), // the initial blurred state is irrelevant
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe((blurred) => {
        if (blurred) {
          this.onBlur();
        }
      });
  }

  private calculateSize(): void {
    this._calculatedSize = this._size;
    this.chRef.markForCheck();
  }

  private scheduleRangeEmissionOnNextBlur(range: DateRange): void {
    this.nextBlurEmissionScheduledSubscription = this.componentIsBlurred$
      .pipe(
        filter((blurred) => blurred),
        take(1)
      )
      .subscribe(() => {
        this.onChange?.(range);
        this.chRef.markForCheck();
      });
  }
}
